/**
 * metaLeadGen/components/KpiCards.tsx
 *
 * Shared KPI card primitives used across Meta Ads sections.
 *
 * Exports:
 *   KpiCard       — standard white card (volumes, totals)
 *   SmallDarkCard — compact black card (ratios, rates, costs)
 */

import { motion } from "framer-motion";

// ─────────────────────────────────────────────
// CHANGE INDICATOR — arrow + text, for white cards
// ─────────────────────────────────────────────

export function ChangeIndicator({ change, invertChange = false }: { change: number; invertChange?: boolean }) {
  const isPositive = invertChange ? change < 0 : change > 0;
  const isNegative = invertChange ? change > 0 : change < 0;
  if (change === 0) return null;
  return (
    <div className={`flex items-center gap-0.5 text-[11px] font-semibold ${
      isPositive ? "text-emerald-400" : isNegative ? "text-red-400" : "text-muted-foreground"
    }`}>
      <svg className="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 20 20">
        {isPositive ? (
          <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
        ) : (
          <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
        )}
      </svg>
      <span>{change > 0 ? "+" : ""}{change.toFixed(1)}%</span>
    </div>
  );
}

// ─────────────────────────────────────────────
// CHANGE TEXT — text-only, for dark cards
// ─────────────────────────────────────────────

export function ChangeText({ change, invertChange = false }: { change: number; invertChange?: boolean }) {
  const isPositive = invertChange ? change < 0 : change > 0;
  const isNegative = invertChange ? change > 0 : change < 0;
  if (change === 0) return null;
  return (
    <span className={`text-[11px] font-semibold ${
      isPositive ? "text-emerald-400" : isNegative ? "text-red-400" : "text-muted-foreground"
    }`}>
      {change > 0 ? "+" : ""}{change.toFixed(1)}%
    </span>
  );
}

// ─────────────────────────────────────────────
// STANDARD KPI CARD — white, for volumes and totals
// ─────────────────────────────────────────────

export interface KpiCardProps {
  label: string;
  value: string;
  change: number;
  index: number;
  invertChange?: boolean;
}

export function KpiCard({ label, value, change, index, invertChange = false }: KpiCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.2 + index * 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
      whileHover={{ y: -2, boxShadow: "0 8px 30px -10px rgba(0,0,0,0.15)", transition: { duration: 0.2 } }}
      className="bg-card rounded-xl border border-border px-3.5 py-3 backdrop-blur-sm hover:border-border/80 hover:bg-card/80 transition-colors duration-300 flex flex-col justify-between min-h-[76px] h-full"
    >
      <p className="text-[9.5px] font-semibold text-muted-foreground uppercase tracking-wider leading-tight">
        {label}
      </p>
      <div className="mt-1.5">
        <span className="text-[18px] font-bold text-foreground leading-none block">{value}</span>
        <div className="mt-0.5">
          <ChangeIndicator change={change} invertChange={invertChange} />
        </div>
      </div>
    </motion.div>
  );
}

// ─────────────────────────────────────────────
// SMALL DARK CARD — black, for rates, ratios, costs
// ─────────────────────────────────────────────

export interface SmallDarkCardProps {
  label: string;
  value: string;
  change: number;
  index: number;
  invertChange?: boolean;
}

export function SmallDarkCard({ label, value, change, index, invertChange = false }: SmallDarkCardProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.4, delay: 0.2 + index * 0.06, ease: [0.25, 0.46, 0.45, 0.94] }}
      whileHover={{ y: -2, boxShadow: "0 8px 30px -10px rgba(0,0,0,0.4)", transition: { duration: 0.2 } }}
      className="bg-secondary rounded-xl px-3 py-3 flex flex-col justify-between h-full"
    >
      <p className="text-[10px] font-semibold text-secondary-foreground/60 uppercase tracking-widest">
        {label}
      </p>
      <div className="mt-2">
        <span className="text-[17px] font-bold text-secondary-foreground leading-none block">{value}</span>
        <div className="mt-1">
          <ChangeText change={change} invertChange={invertChange} />
        </div>
      </div>
    </motion.div>
  );
}
